import { useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { SubsetSuggestionStatus } from '../../../../../../../backend/src/case/subset/subset-suggestion.entity';
import { useUpdateSuggestionStatus } from '@/api/mutations/useUpdateSuggestionStatus';
import { useGetSuggestedSubsets } from '@/api/queries/useGetSuggestedSubsets';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Empty, EmptyHeader, EmptyMedia, EmptyTitle, EmptyDescription } from '@/new/components/shadcn/empty';

export function SuggestedSubset() {
  const { matterId } = useParams<{ matterId: string }>();
  const navigate = useNavigate();
  const { data: suggestions, isLoading } = useGetSuggestedSubsets();
  const { mutate: updateStatus, isLoading: isUpdating } = useUpdateSuggestionStatus();
  const [showAll, setShowAll] = useState(false);

  const handleCreateSubset = (suggestionId: string) => {
    navigate(`/matters/${matterId}/subsets/new?suggestionId=${suggestionId}`);
  };

  if (isLoading) {
    return null;
  }

  return (
    <div className="flex h-fit flex-col rounded-lg border bg-gray-50 p-3">
      <h2 className="mt-1 mb-3 flex items-center gap-2 px-1 font-medium text-gray-600">
        <Icon className="lucide--sparkles" />
        Suggested Subsets
      </h2>
      {suggestions?.length ? (
        <div className="space-y-2">
          {suggestions.slice(0, showAll ? undefined : 3).map((suggestion) => (
            <div
              key={suggestion.id}
              className="group relative rounded-md border border-gray-200 bg-white px-4 py-2 pr-2"
            >
              <div className="flex items-center justify-between gap-5">
                <div>
                  <div className="mb-1 flex items-center gap-1.5">
                    <Icon className="lucide--sparkles text-sm text-blue-600" />
                    <h3 className="text-sm font-medium text-gray-900">{suggestion.name}</h3>
                  </div>
                  <p className="text-xs text-gray-600">{suggestion.description}</p>
                </div>

                <div className="flex flex-col items-end">
                  <Button size="sm" onClick={() => handleCreateSubset(suggestion.id)} disabled={isUpdating}>
                    <Icon className="lucide--plus" />
                    Create subset
                  </Button>

                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-gray-500"
                    onClick={() =>
                      updateStatus({
                        suggestionId: suggestion.id,
                        status: 'dismissed' as SubsetSuggestionStatus.Dismissed,
                      })
                    }
                    disabled={isUpdating}
                  >
                    Dismiss
                  </Button>
                </div>
              </div>
            </div>
          ))}

          {!showAll && suggestions.length > 3 && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-xs text-gray-500 hover:text-gray-900"
              onClick={() => setShowAll(true)}
            >
              Show {suggestions.length - 3} more suggestions
            </Button>
          )}
        </div>
      ) : (
        <Empty className="rounded-lg border border-dashed border-gray-200 bg-white py-8">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Icon className="lucide--sparkles" />
            </EmptyMedia>
            <EmptyTitle className="text-base">No suggested Subsets</EmptyTitle>
            <EmptyDescription>Wexler suggests Subsets when you add new documents</EmptyDescription>
          </EmptyHeader>
        </Empty>
      )}
    </div>
  );
}
